// src/components/AnnotationPanel.jsx
//
// Right-side panel for creating annotation layers.
// Opens when the user clicks "Create" in My Layers (LayerPanel).
// On save, hands the GeoJSON FeatureCollection to useUserLayersStore
// via the same saveAnnotationLayer action used for uploaded files.
//
// Map interaction (clicks) is wired through useAnnotationStore —
// MapView listens for the open/tool state and forwards map clicks here.

import React, { useState, useRef } from "react";
import {
  X,
  MapPin,
  Minus,
  Pentagon,
  StickyNote,
  Trash2,
  Pencil,
  Save,
  ChevronDown,
  Check,
  CheckCircle2,
  AlertCircle,
  MousePointerClick,
  CornerDownLeft,
  Move,
  Spline,
} from "lucide-react";
import { useAnnotationStore } from "../../stores/useAnnotationStore";
import { useUserLayersStore } from "../../stores/useUserLayersStore";
import { useProjectStore } from "../../stores/useProjectStore";

/* ─── helpers ─────────────────────────────────────────── */
function cx(...c) { return c.filter(Boolean).join(" "); }

const TOOLS = [
  { id: "pin",     label: "Point",     Icon: MapPin,          hint: "Click the map to place a pin" },
  { id: "line",    label: "Line",    Icon: Minus,           hint: "Click two points to draw a line" },
  { id: "polygon", label: "Poly",    Icon: Pentagon,        hint: "Click points · double-click to close" },
  { id: "note",    label: "Note",    Icon: StickyNote,      hint: "Click the map · enter note text" },
];

const COLORS = [
  { hex: "#16f2d4", label: "Teal"   },
  { hex: "#f97316", label: "Orange" },
  { hex: "#e24b4a", label: "Red"    },
  { hex: "#7f77dd", label: "Purple" },
  { hex: "#378add", label: "Blue"   },
  { hex: "#1d9e75", label: "Green"  },
  { hex: "#ef9f27", label: "Amber"  },
  { hex: "#888780", label: "Gray"   },
];

const TYPE_LABEL = { pin: "Point", line: "Line", polygon: "Poly", note: "Note" };

/* ─── per-annotation colour swatch ───────────────────────
   Same construction as FeatureColorSwatch in the LayerPanel: a coloured box
   with a transparent <input type="color"> stretched over it, so the box IS
   the control and the browser supplies the picker. Shows the effective
   colour, so it doubles as the row's legend.

   Free-form rather than the eight-swatch palette used for the session
   colour above. The palette is a quick pick for what you are about to
   draw; this is a correction to one existing feature, where being able to
   match something exactly is worth more than staying on-palette. */
function AnnColorSwatch({ color, onChange, label }) {
  return (
    <label
      className="relative cursor-pointer leading-none flex-shrink-0"
      title="Set color"
      onClick={(e) => e.stopPropagation()}   // don't start a rename
    >
      <span
        style={{ background: color }}
        className="block w-3.5 h-3.5 rounded-sm border border-slate-600"
      />
      <input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-label={`Color for ${label}`}
      />
    </label>
  );
}

/* ─── annotation list item ────────────────────────────────
   Click the name to rename it in place — Enter commits, Escape cancels,
   blur cancels, and the tick commits for the mouse path (onMouseDown, so it
   lands before the input's blur).

   This is a single click, unlike the double-click on saved-layer rows in
   MyLayersTab. The two lists differ because the gesture is not competing
   for the same click: a saved-layer row toggles the layer's visibility, so
   rename had to move out of the way onto a double-click. An annotation row
   only selects, and starting a rename selects it too — so nothing is lost
   by taking the single click, and the cheaper gesture belongs on the list
   people are actively editing. Clicking the row outside the name still
   selects without entering edit mode. */
function AnnRow({
  ann, selected, renaming, draft, setDraft,
  onSelect, onDelete, onRecolor, onStartRename, onCommitRename, onCancelRename,
}) {
  return (
    <div className={cx(renaming && "rounded border border-accent-info/40 bg-accent-info/5")}>
    <div
      onClick={() => !renaming && onSelect(ann.id)}
      className={cx(
        "group flex items-center gap-2 px-2 py-[6px] rounded border transition cursor-pointer",
        selected
          ? "border-accent-action/60"
          : "border-slate-800 bg-bg hover:border-slate-700"
      )}
    >
      <AnnColorSwatch
        color={ann.color}
        label={ann.label}
        onChange={(hex) => onRecolor(ann.id, hex)}
      />

      {renaming ? (
        <input
          autoFocus
          aria-label="Annotation name"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onCommitRename(ann.id, draft);
            if (e.key === "Escape") onCancelRename();
          }}
          // Deliberately NO onBlur handler. Edit mode is explicit now — in
          // via the pencil, out via the tick, the cross, Enter or Escape —
          // and the whole point of it is to go work on the map. Cancelling
          // on blur meant that clicking a vertex handle, or dragging a pin,
          // tore down the mode the click was trying to use. Leaving the
          // input unfocused while the mode stays open is the correct
          // resting state: the draft is held in renameVal on the panel, so
          // nothing is lost while the user is out on the map.
          maxLength={60}
          className="flex-1 min-w-0 bg-bg-softer border border-slate-700 rounded px-1 py-px
                     text-[11px] text-gray-200 focus:outline-none focus:border-accent-info/60"
        />
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); onStartRename(ann.id, ann.label); }}
          title="Click to rename"
          className={cx(
            "flex-1 text-left text-[11px] truncate min-w-0",
            selected ? "text-gray-100" : "text-gray-300"
          )}
        >
          {ann.label}
        </button>
      )}

      {/* The type badge yields its space to the input while renaming — the
          row is 288px wide and the name needs the room more than the badge
          does for those few seconds. */}
      {!renaming && (
        <span className="text-[9px] font-mono text-fg-secondary flex-shrink-0">
          {TYPE_LABEL[ann.type]}
        </span>
      )}


      {/* Action cluster. Edit/delete swap for save/cancel in place, so the
          buttons never move under the cursor mid-gesture.

          Plain onClick. These were onMouseDown while the input cancelled on
          blur — blur fires before click, so the buttons were unmounted
          before their handler ran and save silently discarded the edit.
          With no blur handler that hazard is gone, and onClick is the right
          choice anyway: a keyboard Enter on a focused button fires click,
          never mousedown. */}
      {renaming ? (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onCommitRename(ann.id, draft); }}
            className="flex-shrink-0 p-0.5 rounded bg-accent-action/20 hover:bg-accent-action/30
                       text-accent-action border border-accent-action/40 transition"
            title="Save name"
          >
            <Check size={11} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onCancelRename(); }}
            className="flex-shrink-0 p-0.5 rounded border border-transparent
                       hover:bg-slate-700 text-fg-secondary hover:text-gray-200 transition"
            title="Cancel"
          >
            <X size={11} />
          </button>
        </>
      ) : (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onStartRename(ann.id, ann.label); }}
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-slate-700
                       text-fg-secondary hover:text-accent-action transition flex-shrink-0"
            title="Rename"
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(ann.id); }}
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-slate-700
                       text-fg-secondary hover:text-red-400 transition flex-shrink-0"
            title="Delete"
          >
            <Trash2 size={11} />
          </button>
        </>
      )}
    </div>

    {/* What "editing" actually gets you, stated per type while the mode is
        open. Sitting directly under the row keeps it attached to the thing
        it describes, and it costs no height when nothing is being edited.

        Areas and lines both get MlDraw's vertex handles, so they share a
        hint — and it spells out the two gestures that are not guessable
        from looking at a handle: right-click deletes one, and clicking the
        segment between two inserts one. */}
    {renaming && (
      <div className="flex items-center gap-1.5 px-2 py-1 mt-0.5">
        {(ann.type === "polygon" || ann.type === "line") && (
          <>
            <Spline size={10} className="text-accent-info flex-shrink-0" />
            <span className="text-[10px] text-gray-400">
              Drag handles · right-click removes · click the line to add
            </span>
          </>
        )}
        {(ann.type === "pin" || ann.type === "note") && (
          <>
            <Move size={10} className="text-accent-info flex-shrink-0" />
            <span className="text-[10px] text-gray-400">Drag it on the map to move</span>
          </>
        )}
      </div>
    )}
    </div>
  );
}

/* ─── main panel ──────────────────────────────────────── */
export default function AnnotationPanel() {
  const open           = useAnnotationStore(s => s.open);
  const tool           = useAnnotationStore(s => s.tool);
  const color          = useAnnotationStore(s => s.color);
  const pendingPoints  = useAnnotationStore(s => s.pendingPoints);
  const annotations    = useAnnotationStore(s => s.annotations);
  const layerName      = useAnnotationStore(s => s.layerName);
  const selectedId     = useAnnotationStore(s => s.selectedId);
  const editingId      = useAnnotationStore(s => s.editingId);

  const setTool        = useAnnotationStore(s => s.setTool);
  const setColor       = useAnnotationStore(s => s.setColor);
  const setLayerName   = useAnnotationStore(s => s.setLayerName);
  const closePanel     = useAnnotationStore(s => s.closePanel);
  const deleteAnnotation = useAnnotationStore(s => s.deleteAnnotation);
  const clearAll       = useAnnotationStore(s => s.clearAll);
  const closePendingPolygon = useAnnotationStore(s => s.closePendingPolygon);
  const closePendingLine = useAnnotationStore(s => s.closePendingLine);
  const cancelPending  = useAnnotationStore(s => s.cancelPending);
  const toGeoJSON      = useAnnotationStore(s => s.toGeoJSON);
  const selectAnnotation = useAnnotationStore(s => s.selectAnnotation);
  const renameAnnotation = useAnnotationStore(s => s.renameAnnotation);
  const recolorAnnotation = useAnnotationStore(s => s.recolorAnnotation);
  const setEditingId = useAnnotationStore(s => s.setEditingId);

  const saveAnnotationLayer = useUserLayersStore(s => s.saveAnnotationLayer);
  const updateAnnotationLayer = useUserLayersStore(s => s.updateAnnotationLayer);
  const editingLayerId = useAnnotationStore(s => s.editingLayerId);
  const projectId = useProjectStore(s => s.activeProjectId);

  const [saving, setSaving] = useState(false);
  const [savedName, setSavedName] = useState(null); // flash on success
  const [error, setError] = useState(null);
  // Only the draft TEXT is local. Which annotation is being edited lives in
  // the store as editingId — there is no second local "renamingId".
  //
  // It used to be local, and that was the bug: the store cleared editingId
  // (on selecting another annotation, or picking a draw tool) while the
  // panel's copy stayed put, so a row could sit in rename mode for an
  // annotation nothing was editing. One concept, one owner.
  const [renameVal, setRenameVal] = useState("");
  const nameRef = useRef(null);

  if (!open) return null;

  const activeTool = TOOLS.find(t => t.id === tool);
  const canSave = annotations.length > 0 && layerName.trim().length > 0;

  // The pencil opens ONE edit state covering both halves: the name goes
  // editable in place, and the geometry goes live — vertex handles for an
  // area, draggable for a pin or note. There is no second "edit shape"
  // step, so the user never has to know that those are two mechanisms
  // underneath (a store rename vs. the shared MlDraw instance).
  function startEdit(id, label) {
    selectAnnotation(id);
    setEditingId(id);
    setRenameVal(label);
  }

  // Save. Geometry has been written straight to the store all along — a
  // vertex drag and a pin drag are both live — so this commits the name and
  // closes the mode. Nothing to flush.
  function commitRename(id, value) {
    renameAnnotation(id, value);   // ignores an empty/whitespace name
    setEditingId(null);
  }

  // Cancel. Discards the name draft only. Geometry changes made during the
  // session are already committed and are NOT rolled back — undoing them
  // would need a snapshot taken on entry, which is a bigger promise than
  // this button should imply.
  function cancelEdit() {
    setEditingId(null);
  }

  async function handleSave() {
    if (!canSave) {
      nameRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const geojson = toGeoJSON();
      const name = layerName.trim();

      if (editingLayerId) {
        // Updating a layer opened from My Layers.
        //
        // The session is deliberately NOT cleared and editingLayerId is NOT
        // dropped. The user is still looking at the layer they just saved, so
        // wiping the panel would empty the list while the map stays drawn —
        // and releasing the claim would make the NEXT save create a duplicate
        // instead of updating the same layer again. Edit mode ends when the
        // panel closes, which is the gesture that actually means "done".
        await updateAnnotationLayer({ layerId: editingLayerId, name, geojson, color });
        setSavedName(name);
      } else {
        await saveAnnotationLayer({ projectId, name, geojson, color });
        setSavedName(name);
        // Reset session
        clearAll();
        setLayerName("");
        setTool(null);
      }
      setTimeout(() => setSavedName(null), 3000);
    } catch (err) {
      console.error("[AnnotationPanel] save failed", err);
      setError(editingLayerId
        ? "Failed to update — please try again."
        : "Failed to save — please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    /* Slide-in from the right — sits in the absolute map area.
       top-[180px] clears the minimap (w-40 h-40 at top-[10px] right-3 = 170px bottom edge).
       z-[90] sits above the minimap's z-[80] so the panel header is never obscured
       if the user resizes the window and they overlap.

       The cap is 100% — the map <section> this is positioned inside — NOT 100vh.
       The section starts below App's h-14 header (and below the impersonation /
       project banners when those show), so a viewport-relative cap let the panel
       hang 36px+ past the section's bottom edge, where MapPage's overflow-hidden
       clipped the footer away with no way to scroll to it. 190px = the 180px top
       offset + a 10px bottom gutter matching the minimap's. */
    <div className="absolute top-[180px] right-0 z-[90] flex flex-col w-72
                    bg-bg-softer border border-slate-700 rounded-l-lg
                    shadow-2xl shadow-black/60
                    animate-slide-in-right overflow-hidden
                    max-h-[calc(100%-190px)]">

      {/* ── HEADER ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-3 border-b border-slate-700 flex-shrink-0">
        <div className="flex-1">
          <div className="text-[11px] font-semibold text-gray-200 leading-tight">
            {editingLayerId ? "Edit Annotation Layer" : "Create Annotation Layer"}
          </div>
          <div className="text-[10px] text-fg-secondary leading-tight mt-0.5">
            {annotations.length === 0
              ? "Select a tool and draw on the map"
              : `${annotations.length} annotation${annotations.length !== 1 ? "s" : ""}`}
          </div>
        </div>
        <button
          onClick={closePanel}
          className="p-1 rounded hover:bg-slate-700 text-fg-secondary hover:text-gray-200 transition flex-shrink-0"
        >
          <X size={14} />
        </button>
      </div>

      {/* ── CONTROLS — fixed ────────────────────────────────
           Tool grid, colors and the list header stay put. They used to sit
           inside the scrolling region, so a long list carried them out of
           view and switching tools mid-session meant scrolling back up. */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2 space-y-4">

        {/* TOOLS */}
        <div>
          <div className="text-[10px] font-bold font-mono uppercase tracking-[0.12em] text-fg-secondary mb-2">
            Draw tool
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {TOOLS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTool(tool === id ? null : id)}
                className={cx(
                  "flex flex-col items-center gap-1 py-2 rounded-md border text-[10px] transition",
                  tool === id
                    ? "bg-accent-action/15 border-accent-action/50 text-accent-action"
                    : "bg-bg border-slate-700 text-gray-400 hover:border-slate-600 hover:text-gray-200"
                )}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>

          {/* Contextual hint */}
          {activeTool && (
            <div className="mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded bg-slate-800/60 border border-slate-700/60">
              <MousePointerClick size={11} className="text-accent-info flex-shrink-0" />
              <span className="text-[10px] text-gray-400">{activeTool.hint}</span>
            </div>
          )}

          {/* Polygon close button — appears when 3+ points collected */}
          {tool === "polygon" && pendingPoints.length >= 3 && (
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={closePendingPolygon}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded border
                           border-accent-action/50 bg-accent-action/10 text-accent-action text-[11px]
                           hover:bg-accent-action/20 transition"
              >
                <CornerDownLeft size={12} />
                Close polygon ({pendingPoints.length} pts)
              </button>
              <button
                onClick={cancelPending}
                className="p-1.5 rounded border border-slate-700 text-fg-secondary hover:text-red-400 hover:border-red-500/40 transition"
                title="Cancel"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {/* Line first-point indicator */}
          {tool === "line" && pendingPoints.length === 1 && (
            <div className="mt-2 flex items-center gap-1.5 px-2 py-1.5 rounded bg-orange-500/10 border border-orange-500/30">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
              <span className="text-[10px] text-orange-300">Start point set — click to add points</span>
              <button onClick={cancelPending} className="ml-auto p-0.5 rounded text-orange-400/60 hover:text-orange-400">
                <X size={10} />
              </button>
            </div>
          )}

          {/* Line finish button — the same affordance the polygon has, and now
              REQUIRED rather than a convenience. A line used to commit itself on
              the second click; ending one is a deliberate act now, and
              double-click is the only other way. A user who does not discover
              that gesture would otherwise have no way to finish a line at all,
              which would make the tool less usable than before this change. */}
          {tool === "line" && pendingPoints.length >= 2 && (
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={closePendingLine}
                className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded border
                           border-accent-action/50 bg-accent-action/10 text-accent-action text-[11px]
                           hover:bg-accent-action/20 transition"
              >
                <CornerDownLeft size={12} />
                Finish line ({pendingPoints.length} pts)
              </button>
              <button
                onClick={cancelPending}
                className="p-1.5 rounded border border-slate-700 text-fg-secondary hover:text-red-400 hover:border-red-500/40 transition"
                title="Cancel"
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>

        {/* COLOR */}
        <div>
          <div className="text-[10px] font-bold font-mono uppercase tracking-[0.12em] text-fg-secondary mb-2">
            Color
          </div>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(c => (
              <button
                key={c.hex}
                onClick={() => setColor(c.hex)}
                title={c.label}
                className={cx(
                  "w-6 h-6 rounded-full border-2 transition",
                  color === c.hex
                    ? "border-white scale-110"
                    : "border-transparent hover:scale-105"
                )}
                style={{ background: c.hex }}
              />
            ))}
          </div>
        </div>

        {/* LIST HEADER — fixed, so "clear all" is always reachable */}
        {annotations.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold font-mono uppercase tracking-[0.12em] text-fg-secondary flex-1">
              Annotations
            </span>
            <button
              onClick={clearAll}
              className="text-[9px] text-red-500/70 hover:text-red-400 transition"
            >
              clear all
            </button>
          </div>
        )}

      </div>

      {/* ── ANNOTATION ROWS — the only scrolling region ─────
           min-h-0 is belt-and-braces: overflow-y-auto already zeroes the
           automatic minimum size, but the pair is how the rest of the app
           writes a flex scroll pane (HeliosDock.jsx:265). */}
      {annotations.length > 0 && (
        <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-1">
          {annotations.map(ann => (
            <AnnRow
              key={ann.id}
              ann={ann}
              selected={ann.id === selectedId}
              renaming={ann.id === editingId}
              draft={renameVal}
              setDraft={setRenameVal}
              onSelect={selectAnnotation}
              onDelete={deleteAnnotation}
              onRecolor={recolorAnnotation}
              onStartRename={startEdit}
              onCommitRename={commitRename}
              onCancelRename={cancelEdit}
            />
          ))}
        </div>
      )}

      {/* ── FLASH — fixed above the footer ──────────────────
           Save feedback rode the scrolling body before, so a failed save
           could report an error the user had to scroll to find. */}
      {(savedName || error) && (
        <div className="flex-shrink-0 px-3 pb-3 space-y-2">
          {savedName && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-green-500/40 bg-green-500/10">
              <CheckCircle2 size={13} className="text-green-400 flex-shrink-0" />
              <span className="text-[11px] text-green-300">
                "{savedName}" {editingLayerId ? "updated" : "saved to My Layers"}
              </span>
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-red-500/40 bg-red-500/10">
              <AlertCircle size={13} className="text-red-400 flex-shrink-0" />
              <span className="text-[11px] text-red-300">{error}</span>
            </div>
          )}
        </div>
      )}

      {/* ── FOOTER — LAYER NAME + SAVE ─────────────────────── */}
      <div className="flex-shrink-0 px-3 py-3 border-t border-slate-700 space-y-2">
        <input
          ref={nameRef}
          type="text"
          value={layerName}
          onChange={e => setLayerName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") handleSave(); }}
          placeholder="Layer name…"
          maxLength={60}
          className={cx(
            "w-full px-2.5 py-2 rounded-md border bg-bg text-[12px] text-gray-200",
            "placeholder-gray-600 focus:outline-none transition",
            layerName
              ? "border-accent-info/50 focus:border-accent-info"
              : "border-slate-700 focus:border-slate-500"
          )}
        />
        <button
          onClick={handleSave}
          disabled={!canSave || saving}
          className={cx(
            "w-full py-2 rounded-md border text-[12px] font-medium transition",
            canSave && !saving
              ? "bg-accent-action/15 border-accent-action/50 text-accent-action hover:bg-accent-action/25"
              : "bg-bg border-slate-700 text-gray-600 cursor-not-allowed"
          )}
        >
          {saving ? (editingLayerId ? "Updating…" : "Saving…") : (
            <span className="flex items-center justify-center gap-1.5">
              <Save size={13} />
              {editingLayerId ? "Update layer" : "Save to My Layers"}
            </span>
          )}
        </button>

        {!canSave && annotations.length === 0 && (
          <p className="text-center text-[10px] text-fg-secondary">
            Draw at least one annotation to save
          </p>
        )}
        {!canSave && annotations.length > 0 && !layerName.trim() && (
          <p className="text-center text-[10px] text-fg-secondary">
            Enter a layer name above
          </p>
        )}
      </div>
    </div>
  );
}
